import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useReducedMotion } from "framer-motion";
import { Pause, Play, RotateCcw, Square, Volume2, VolumeX, CheckCircle2, Mic, Glasses, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MOODS, moodLabel } from "@/lib/api";
import { ImmersiveRoom } from "@/components/ImmersiveRoom";

const MoodPicker = ({ label, value, onChange, testId }) => (
  <div data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{label}</p>
    <div className="flex flex-wrap justify-center gap-2" role="radiogroup" aria-label={label}>
      {MOODS.map((m) => (
        <button key={m.value} type="button" role="radio" aria-checked={value === m.value} onClick={() => onChange(m.value)} data-testid={`${testId}-${m.value}`}
          className={`px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold border transition-colors ${value === m.value ? m.active : `${m.color} hover:brightness-95`}`}>
          {m.label}
        </button>
      ))}
    </div>
  </div>
);

const PHASE_META = {
  inhale: { label: "Breathe in", scale: 1, color: "#6366f1" },
  hold_in: { label: "Hold", scale: 1, color: "#8b5cf6" },
  exhale: { label: "Breathe out", scale: 0.55, color: "#10b981" },
  hold_out: { label: "Hold", scale: 0.55, color: "#14b8a6" },
};
const MIN_SCALE = 0.55;
const DURATIONS = [1, 3, 5];

function buildPhases(cfg) {
  return ["inhale", "hold_in", "exhale", "hold_out"]
    .map((key) => ({ key, seconds: Number(cfg?.[key] ?? 0) }))
    .filter((p) => p.seconds > 0);
}

function playChime(ctxRef, muted) {
  if (muted) return;
  try {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 528;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.9);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 1);
  } catch (e) {
    console.warn("Chime unavailable (Web Audio not supported):", e?.message || e);
  }
}

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const canSpeak = typeof window !== "undefined" && "speechSynthesis" in window;

function speak(text, enabled) {
  if (!enabled || !canSpeak) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.85;
    u.pitch = 1;
    u.volume = 0.9;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) => /Samantha|Google UK English Female|Karen|Moira|Serena/i.test(v.name))
      || voices.find((v) => v.lang?.startsWith("en") && /female/i.test(v.name))
      || voices.find((v) => v.lang?.startsWith("en"));
    if (preferred) u.voice = preferred;
    window.speechSynthesis.speak(u);
  } catch (e) {
    console.warn("Speech synthesis unavailable:", e?.message || e);
  }
}

export const BreathingExercise = ({ config, onComplete, onSessionEnd, onAskCompanion, alreadyCompleted = false }) => {
  const phases = useMemo(() => buildPhases(config), [config]);
  const reduceMotion = useReducedMotion();
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [voice, setVoice] = useState(() => canSpeak && localStorage.getItem("breath_voice") === "1");
  const [minutes, setMinutes] = useState(3);
  const [moodBefore, setMoodBefore] = useState(null);
  const [moodAfter, setMoodAfter] = useState(null);
  const [afterDone, setAfterDone] = useState(false);
  const [immersive, setImmersive] = useState(false);

  useEffect(() => {
    if (!immersive) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [immersive]);
  const [s, setS] = useState({ mode: "idle", remaining: 180, phaseIdx: 0, phaseLeft: phases[0]?.seconds || 4, rounds: 0, ready: 3 });

  const active = s.mode === "running" || s.mode === "ready";

  useEffect(() => {
    if (!canSpeak) return undefined;
    window.speechSynthesis.getVoices();
    return () => window.speechSynthesis.cancel();
  }, []);

  const toggleVoice = () => {
    setVoice((v) => {
      const next = !v;
      localStorage.setItem("breath_voice", next ? "1" : "0");
      if (next) speak("Voice guidance on", true);
      else if (canSpeak) window.speechSynthesis.cancel();
      return next;
    });
  };

  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => {
      setS((prev) => {
        if (prev.mode === "ready") {
          if (prev.ready <= 1) return { ...prev, mode: "running", ready: 0, phaseIdx: 0, phaseLeft: phases[0].seconds };
          return { ...prev, ready: prev.ready - 1 };
        }
        const remaining = prev.remaining - 1;
        if (remaining <= 0) return { ...prev, remaining: 0, mode: "done" };
        if (prev.phaseLeft <= 1) {
          const next = (prev.phaseIdx + 1) % phases.length;
          return { ...prev, remaining, phaseIdx: next, phaseLeft: phases[next].seconds, rounds: next === 0 ? prev.rounds + 1 : prev.rounds };
        }
        return { ...prev, remaining, phaseLeft: prev.phaseLeft - 1 };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [active, phases]);

  const phaseKey = phases[s.phaseIdx]?.key || "inhale";
  useEffect(() => {
    if (s.mode === "running") {
      playChime(audioRef, muted);
      speak(PHASE_META[phaseKey].label, voice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.phaseIdx, s.mode === "running"]);

  const completedRef = useRef(false);
  useEffect(() => {
    if (s.mode === "done" && !completedRef.current) {
      completedRef.current = true;
      playChime(audioRef, muted);
      speak("Well done. Notice how your body feels.", voice);
      onComplete?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.mode]);

  const loggedRef = useRef(false);
  const finishCheckin = (after) => {
    if (loggedRef.current) return;
    loggedRef.current = true;
    setMoodAfter(after);
    setAfterDone(true);
    onSessionEnd?.({ seconds: minutes * 60, rounds: s.rounds, completed: true, mood_before: moodBefore, mood_after: after });
  };

  const start = () => {
    playChime(audioRef, true);
    speak("Get ready", voice);
    completedRef.current = false;
    loggedRef.current = false;
    setMoodAfter(null);
    setAfterDone(false);
    setS({ mode: "ready", remaining: minutes * 60, phaseIdx: 0, phaseLeft: phases[0].seconds, rounds: 0, ready: 3 });
  };
  const stop = () => {
    if (canSpeak) window.speechSynthesis.cancel();
    const practised = minutes * 60 - s.remaining;
    if (s.mode !== "ready" && practised >= 60 && !loggedRef.current) {
      loggedRef.current = true;
      onSessionEnd?.({ seconds: practised, rounds: s.rounds, completed: false, mood_before: moodBefore, mood_after: null });
    }
    setS((p) => ({ ...p, mode: "idle" }));
  };
  const pause = () => { if (canSpeak) window.speechSynthesis.cancel(); setS((p) => ({ ...p, mode: "paused" })); };

  const phase = phases[s.phaseIdx] || phases[0];
  const meta = PHASE_META[phaseKey];
  let scale = MIN_SCALE;
  if (s.mode === "running" || s.mode === "paused") {
    const progress = (phase.seconds - s.phaseLeft + 1) / phase.seconds;
    if (phaseKey === "inhale") scale = MIN_SCALE + (1 - MIN_SCALE) * Math.min(progress, 1);
    else if (phaseKey === "exhale") scale = 1 - (1 - MIN_SCALE) * Math.min(progress, 1);
    else scale = meta.scale;
  }
  const opacity = reduceMotion ? 0.45 + (scale - MIN_SCALE) : 1;
  const animStyle = reduceMotion
    ? { opacity, transition: s.mode === "running" ? "opacity 1s linear" : "none", background: meta.color }
    : { transform: `scale(${scale})`, transition: s.mode === "running" ? "transform 1s linear, background-color 1s ease" : "transform 0.4s ease", background: meta.color };

  if (phases.length === 0) return <p className="text-slate-500">This breathing pattern has no phases configured.</p>;

  const immersiveLabel = s.mode === "ready" ? "Get ready" : s.mode === "running" ? meta.label : s.mode === "paused" ? "Paused" : s.mode === "done" ? "Well done" : "Ready when you are";
  const immersiveCount = s.mode === "ready" ? s.ready : (s.mode === "running" || s.mode === "paused") ? s.phaseLeft : "";
  const controls = (
    <>
        {s.mode === "idle" && (
          <div className="space-y-5 max-w-md">
            {!immersive && config?.intro && <p className="text-slate-600 leading-relaxed">{config.intro}</p>}
            <p className="text-sm text-slate-500">
              Pattern: {phases.map((p) => `${PHASE_META[p.key].label.toLowerCase()} ${p.seconds}s`).join(" · ")}
            </p>
            <MoodPicker label="How do you feel right now?" value={moodBefore} onChange={setMoodBefore} testId="mood-before" />
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Session length</p>
              <div className="flex justify-center gap-2" role="radiogroup">
                {DURATIONS.map((m) => (
                  <button key={m} type="button" role="radio" aria-checked={minutes === m} onClick={() => setMinutes(m)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold border transition-colors ${minutes === m ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:text-indigo-700"}`}
                    data-testid={`breathing-duration-${m}`}>
                    {m} min
                  </button>
                ))}
              </div>
            </div>
            <Button size="lg" onClick={start} className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-8" data-testid="breathing-start-button">
              <Play className="w-4 h-4" /> Start
            </Button>
            {alreadyCompleted && <p className="text-xs text-emerald-700 font-semibold flex items-center justify-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> You have completed this exercise</p>}
          </div>
        )}

        {(s.mode === "running" || s.mode === "paused" || s.mode === "ready") && (
          <div className="w-full max-w-md space-y-5">
            <div className="flex justify-center gap-8 text-sm">
              <div><p className="text-xs uppercase tracking-widest text-slate-500">Round</p><p className="font-display text-2xl font-bold text-slate-900" data-testid="breathing-rounds">{s.rounds + (s.mode === "ready" ? 0 : 1)}</p></div>
              <div><p className="text-xs uppercase tracking-widest text-slate-500">Remaining</p><p className="font-display text-2xl font-bold text-slate-900" data-testid="breathing-remaining">{fmt(s.remaining)}</p></div>
            </div>
            <div className="flex justify-center gap-2 flex-wrap">
              {s.mode === "running" && <Button variant="outline" className="rounded-full" onClick={pause} data-testid="breathing-pause-button"><Pause className="w-4 h-4" /> Pause</Button>}
              {s.mode === "paused" && <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setS((p) => ({ ...p, mode: "running" }))} data-testid="breathing-resume-button"><Play className="w-4 h-4" /> Resume</Button>}
              <Button variant="ghost" className="rounded-full text-slate-600 hover:text-rose-600" onClick={stop} data-testid="breathing-stop-button"><Square className="w-4 h-4" /> Stop</Button>
            </div>
          </div>
        )}

        {s.mode === "done" && (
          <div className="space-y-4 max-w-md rise" data-testid="breathing-done">
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Well done</h3>
            <p className="text-slate-600">You practised for {minutes} minute{minutes > 1 ? "s" : ""} and completed {s.rounds} round{s.rounds === 1 ? "" : "s"}. Notice how your body feels right now.</p>
            {!afterDone ? (
              <div className="rounded-2xl bg-white/80 border border-slate-200 p-4">
                <MoodPicker label="And now — how do you feel?" value={moodAfter} onChange={finishCheckin} testId="mood-after" />
                <button type="button" onClick={() => finishCheckin(null)} className="mt-3 text-xs text-slate-500 hover:text-slate-800 underline" data-testid="mood-after-skip">Skip</button>
              </div>
            ) : (
              <>
                {moodBefore && moodAfter && (
                  <p className="text-sm font-semibold text-indigo-700" data-testid="mood-shift">
                    {moodAfter > moodBefore ? `From ${moodLabel(moodBefore).toLowerCase()} to ${moodLabel(moodAfter).toLowerCase()} — a real shift.` : moodAfter === moodBefore ? `Still ${moodLabel(moodAfter).toLowerCase()} — steady is good too.` : `From ${moodLabel(moodBefore).toLowerCase()} to ${moodLabel(moodAfter).toLowerCase()}. Some days are like that; you still showed up.`}
                  </p>
                )}
                <div className="flex flex-wrap justify-center gap-2">
                  <Button variant="outline" className="rounded-full" onClick={start} data-testid="breathing-again-button"><RotateCcw className="w-4 h-4" /> Practise again</Button>
                  {onAskCompanion && (
                    <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => onAskCompanion({ minutes, rounds: s.rounds, mood_before: moodBefore, mood_after: moodAfter })} data-testid="ask-companion-button"><Sparkles className="w-4 h-4" /> Ask the Companion</Button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
    </>
  );

  const toggles = (
        <div className="absolute top-0 right-0 flex gap-1">
          <button type="button" onClick={() => setImmersive((v) => !v)} className={`h-10 px-3 rounded-full flex items-center gap-1.5 text-xs font-semibold transition-colors ${immersive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white hover:text-indigo-700"}`} aria-pressed={immersive} title="Immersive 3D room (works with VR headsets)" data-testid="breathing-immersive-button">
            <Glasses className="w-4 h-4" /> {immersive ? "Exit room" : "Immersive room"}
          </button>
          {canSpeak && (
            <button type="button" onClick={toggleVoice} className={`w-10 h-10 rounded-full grid place-items-center transition-colors ${voice ? "bg-indigo-600 text-white hover:bg-indigo-700" : "text-slate-500 hover:bg-white hover:text-indigo-700"}`} aria-label={voice ? "Turn voice guidance off" : "Turn voice guidance on"} aria-pressed={voice} title="Spoken cues" data-testid="breathing-voice-button">
              <Mic className="w-5 h-5" />
            </button>
          )}
          <button type="button" onClick={() => setMuted((m) => !m)} className="w-10 h-10 rounded-full grid place-items-center text-slate-500 hover:bg-white hover:text-indigo-700 transition-colors" aria-label={muted ? "Unmute chime" : "Mute chime"} title="Chime" data-testid="breathing-mute-button">
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>
  );

  if (immersive) {
    return createPortal(
      <div className="fixed inset-0 z-[60]" data-testid="breathing-exercise">
        <ImmersiveRoom scale={(scale - MIN_SCALE) / (1 - MIN_SCALE)} color={meta.color} label={immersiveLabel} count={immersiveCount} running={s.mode === "running" || s.mode === "ready"} onExit={() => setImmersive(false)} />
        <div className="absolute bottom-0 inset-x-0 p-3 sm:p-5 flex justify-center pointer-events-none">
          <div className="pointer-events-auto relative w-full max-w-xl rounded-3xl bg-white/90 backdrop-blur-xl border border-white/60 shadow-2xl p-5 sm:p-6 pt-12 flex flex-col items-center text-center gap-4" data-testid="immersive-controls">
            {toggles}
            {controls}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-b from-indigo-50/70 via-white to-emerald-50/60 p-6 sm:p-10 grain" data-testid="breathing-exercise">
      <div className="relative flex flex-col items-center text-center gap-6">
        <div className="relative w-56 h-56 sm:w-72 sm:h-72 grid place-items-center">
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-indigo-200/80" />
          <div className="absolute inset-[22%] rounded-full border border-emerald-200/80" />
          <div aria-hidden className="w-[88%] h-[88%] rounded-full shadow-[0_20px_60px_-15px_rgba(79,70,229,0.5)] will-change-transform" style={animStyle} data-testid="breathing-circle" />
          <div className="absolute inset-0 grid place-items-center text-white drop-shadow pointer-events-none">
            {s.mode === "ready" && (
              <div><p className="text-sm font-semibold uppercase tracking-widest text-indigo-900">Get ready</p><p className="font-display text-6xl font-extrabold text-indigo-900" data-testid="breathing-ready-count">{s.ready}</p></div>
            )}
            {(s.mode === "running" || s.mode === "paused") && (
              <div>
                <p className="font-display text-2xl sm:text-3xl font-bold" data-testid="breathing-phase-label">{s.mode === "paused" ? "Paused" : meta.label}</p>
                <p className="font-display text-5xl sm:text-6xl font-extrabold leading-none mt-1" data-testid="breathing-phase-count">{s.phaseLeft}</p>
              </div>
            )}
            {s.mode === "idle" && <p className="font-display text-xl font-bold text-indigo-900/80">Ready when you are</p>}
            {s.mode === "done" && <CheckCircle2 className="w-16 h-16 text-white" />}
          </div>
        </div>
        {controls}
        {toggles}
      </div>
    </section>
  );
};
